import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CalendarClock, MapPin, PenLine, Plus, Timer } from "lucide-react";
import api from "@/lib/api";
import { fmtFecha, fmtHoras, fmtHora, hoyISO } from "@/lib/format";
import { TIPO_FICHAJE, PAUSA_LABEL, fichajeEsReal } from "@/lib/fichajeUi";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SolicitudPicadoManualDialog from "@/components/SolicitudPicadoManualDialog";
import CorreccionFichajeDialog from "@/components/CorreccionFichajeDialog";

function etiquetaDia(dia) {
  if (!dia || dia === hoyISO()) return "Hoy";
  return fmtFecha(`${dia}T12:00:00`);
}

export default function PicadosHoyPanel({ estado, onRefresh, manualInicial, onManualInicialConsumido }) {
  const [manualOpen, setManualOpen] = useState(false);
  const [manualTipo, setManualTipo] = useState(null);
  const [manualTimestamp, setManualTimestamp] = useState(null);
  const [correccion, setCorreccion] = useState(null);
  const [estadoDia, setEstadoDia] = useState(null);

  const [diaEnfoque, setDiaEnfoque] = useState(hoyISO());
  const [manualPendiente, setManualPendiente] = useState(null);

  useEffect(() => {
    if (manualInicial) setManualPendiente(manualInicial);
  }, [manualInicial]);

  const diaObjetivo =
    manualPendiente?.dia
    || manualPendiente?.fecha?.slice(0, 10)
    || (manualOpen && diaEnfoque !== hoyISO() ? diaEnfoque : null)
    || hoyISO();
  const esHoy = diaObjetivo === hoyISO();

  const estadoPanel = useMemo(() => {
    if (!esHoy && estadoDia) return estadoDia;
    return estado;
  }, [esHoy, estadoDia, estado]);

  useEffect(() => {
    if (esHoy) {
      setEstadoDia(null);
      return;
    }
    let cancel = false;
    api.get("/fichajes/estado", { params: { dia: diaObjetivo } }).then(({ data }) => {
      if (!cancel) setEstadoDia(data);
    });
    return () => {
      cancel = true;
    };
  }, [diaObjetivo, esHoy]);

  const abrirManual = useCallback((tipo = null, timestamp = null) => {
    setDiaEnfoque(diaObjetivo);
    setManualTipo(tipo);
    setManualTimestamp(timestamp);
    setManualOpen(true);
  }, [diaObjetivo]);

  const procesadoManualRef = useRef(null);

  useEffect(() => {
    if (!manualPendiente || !estadoPanel) return;
    if (!esHoy && !estadoDia) return;
    const clave = JSON.stringify(manualPendiente);
    if (procesadoManualRef.current === clave) return;
    procesadoManualRef.current = clave;
    const dia = manualPendiente.dia || manualPendiente.fecha?.slice(0, 10);
    if (dia) setDiaEnfoque(dia);
    if (manualPendiente.modificacion && manualPendiente.fichajeId) {
      setCorreccion({
        fichaje_id: Number(manualPendiente.fichajeId),
        timestamp: manualPendiente.fecha,
      });
      setManualPendiente(null);
      onManualInicialConsumido?.();
      return;
    }
    const pendientesDia = estadoPanel.picados_pendientes || [];
    if (pendientesDia.length > 0 && !manualPendiente.modificacion) {
      abrirManual();
    } else {
      abrirManual(manualPendiente.tipo || null, manualPendiente.fecha || null);
    }
    setManualPendiente(null);
    onManualInicialConsumido?.();
  }, [manualPendiente, estadoPanel, estadoDia, esHoy, abrirManual, onManualInicialConsumido]);

  useEffect(() => {
    if (!manualPendiente && !manualOpen) procesadoManualRef.current = null;
  }, [manualPendiente, manualOpen]);

  const refrescar = useCallback(async () => {
    if (!esHoy) {
      const { data } = await api.get("/fichajes/estado", { params: { dia: diaObjetivo } });
      setEstadoDia(data);
    }
    await onRefresh?.();
  }, [diaObjetivo, esHoy, onRefresh]);

  if (!estadoPanel) {
    return (
      <div className="rounded-2xl border border-zinc-200 bg-white p-6 text-center text-sm text-zinc-400">
        Cargando picados de {etiquetaDia(diaObjetivo).toLowerCase()}…
      </div>
    );
  }

  const fichajes = estadoPanel.fichajes_hoy || [];
  const picadosPendientes = estadoPanel.picados_pendientes || [];
  const solicitudesPendientes = estadoPanel.solicitudes_fichaje_pendientes || [];
  const jornada = estadoPanel.horario_jornada;
  const trabajando = estadoPanel.dentro;
  const enPausa = estadoPanel.en_pausa;
  const tituloDia = etiquetaDia(estadoPanel.dia || diaObjetivo);

  const estadoTexto =
    trabajando || enPausa
      ? enPausa
        ? "En pausa"
        : `Trabajando${estadoPanel.hora_entrada ? ` desde ${fmtHora(estadoPanel.hora_entrada)}` : ""}`
      : estadoPanel.ausencia_pendiente
        ? `Ausencia justificada pendiente${
            estadoPanel.ausencia_pendiente.hora_solicitada
              ? ` · desde ${fmtHora(estadoPanel.ausencia_pendiente.hora_solicitada)}`
              : ""
          }`
        : "Fuera de jornada";

  const segFichajes = estadoPanel.segundos_fichajes ?? estadoPanel.segundos_hoy ?? 0;
  const segPendienteAj = estadoPanel.segundos_ausencia_pendiente || 0;
  const segTotal = estadoPanel.segundos_hoy || 0;

  const filasRegistro = [];
  for (const f of fichajes) {
    filasRegistro.push({ kind: "fichaje", sort: f.timestamp, f });
    const tramo = estadoPanel.ausencia_tramo;
    if (
      tramo &&
      f.origen_ausencia_justificada &&
      f.tipo === "salida"
    ) {
      filasRegistro.push({ kind: "ausencia", sort: tramo.inicio, tramo });
    }
  }
  if (
    estadoPanel.ausencia_tramo &&
    !filasRegistro.some((r) => r.kind === "ausencia")
  ) {
    filasRegistro.push({
      kind: "ausencia",
      sort: estadoPanel.ausencia_tramo.inicio,
      tramo: estadoPanel.ausencia_tramo,
    });
  }
  filasRegistro.sort((a, b) => String(a.sort).localeCompare(String(b.sort)));

  return (
    <div className="space-y-4 rounded-2xl border border-indigo-100 bg-white shadow-sm" data-testid="picados-hoy-panel">
      <div className="border-b border-indigo-100/80 bg-gradient-to-r from-indigo-50/80 to-sky-50/50 px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">{tituloDia}</p>
              <p className="text-sm font-medium text-zinc-800">{estadoTexto}</p>
              {estadoPanel.ausencia_pendiente && (trabajando || enPausa) && (
                <p className="mt-0.5 text-[11px] text-violet-700">
                  Ausencia justificada pendiente de autorización
                </p>
              )}
              {jornada?.franjas?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {jornada.franjas.map((f, i) => (
                    <Badge key={i} variant="outline" className="border-indigo-200 bg-white text-[10px] text-indigo-800">
                      {f.inicio}–{f.fin}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center justify-end gap-1.5 text-sky-700">
              <Timer className="h-4 w-4" />
              <span className="font-display text-2xl font-light text-zinc-900">{fmtHoras(segTotal)}</span>
            </div>
            <p className="text-[10px] uppercase tracking-wide text-zinc-400">
              {esHoy ? "Horas hoy" : "Horas del día"}
            </p>
            {segPendienteAj > 0 && (
              <p className="mt-1 max-w-[11rem] text-[10px] leading-snug text-violet-700" data-testid="horas-hoy-desglose">
                {fmtHoras(segFichajes)} trabajadas + {fmtHoras(segPendienteAj)} pendientes = {fmtHoras(segTotal)}
              </p>
            )}
          </div>
        </div>
      </div>

      {picadosPendientes.length > 0 && (
        <div className="mx-4 rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
          <p className="font-medium">
            Faltan {picadosPendientes.length} picado(s) por regularizar
          </p>
          <ul className="mt-2 space-y-1 text-xs text-amber-900/90">
            {picadosPendientes.map((p) => (
              <li key={`${p.tramo}-${p.tipo_fichaje}`}>
                Tramo {p.franja}: {p.tipo_fichaje === "entrada" ? "entrada" : "salida"}
                {p.es_modificacion ? " (corregir cierre automático)" : ""}
              </li>
            ))}
          </ul>
          <Button
            size="sm"
            className="mt-3 w-full bg-amber-600 hover:bg-amber-700"
            onClick={() => abrirManual()}
            data-testid="regularizar-picados-btn"
          >
            <PenLine className="mr-1.5 h-3.5 w-3.5" />
            Regularizar picados faltantes
          </Button>
        </div>
      )}

      {solicitudesPendientes.length > 0 && picadosPendientes.length === 0 && (
        <div className="mx-4 rounded-xl border border-sky-200 bg-sky-50/80 px-4 py-3 text-sm text-sky-950">
          <p className="font-medium">{solicitudesPendientes.length} picado(s) en revisión por el encargado</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {solicitudesPendientes.map((s) => (
              <li key={s.solicitud_id}>
                {s.tipo_fichaje === "entrada" ? "Entrada" : "Salida"} a las {s.hora}
              </li>
            ))}
          </ul>
          {estadoPanel.dentro && !solicitudesPendientes.some((s) => s.tipo_fichaje === "salida") && (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Button
                size="sm"
                variant="outline"
                className="border-sky-300 text-sky-800 hover:bg-sky-100"
                onClick={() => abrirManual("salida")}
              >
                Solicitar salida manual
              </Button>
            </div>
          )}
          <p className="mt-2 text-xs text-sky-800/90">
            Los picados en revisión ya cuentan para tus horas. Puedes seguir fichando desde Fichar (Salir / Pausa) o
            añadir otros picados aquí.
          </p>
        </div>
      )}

      <div className="px-4 pb-2">
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
            Registro de {esHoy ? "hoy" : tituloDia.toLowerCase()}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            onClick={() => abrirManual(estadoPanel.dentro ? "salida" : "entrada")}
            data-testid="anadir-picado-btn"
          >
            <Plus className="h-3.5 w-3.5" />
            Añadir picado
          </Button>
        </div>

        {filasRegistro.length === 0 ? (
          <p className="rounded-xl border border-dashed border-zinc-200 py-8 text-center text-sm text-zinc-400">
            Aún no hay picados este día. Usa «Añadir picado» si olvidaste fichar.
          </p>
        ) : (
          <div className="space-y-2" data-testid="picados-hoy-lista">
            {filasRegistro.map((row) => {
              if (row.kind === "ausencia") {
                const t = row.tramo;
                return (
                  <div
                    key={`ausencia-${t.inicio}`}
                    className="flex items-center justify-between gap-2 rounded-xl border border-violet-200 bg-violet-50/70 px-3 py-2.5 text-sm"
                    data-testid="picado-ausencia-justificada"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant="outline" className="border-violet-300 bg-violet-100 text-violet-900">
                          Ausencia justificada
                        </Badge>
                        {t.estado === "pendiente" && (
                          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-800">
                            Pendiente autorización
                          </Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-[11px] text-violet-800/80">
                        {t.franja_inicio}–{t.franja_fin}
                        {t.segundos ? ` · ${fmtHoras(t.segundos)}` : ""}
                      </p>
                    </div>
                    <span className="shrink-0 font-medium tabular-nums text-violet-950">
                      {fmtHora(t.inicio)}
                    </span>
                  </div>
                );
              }
              const f = row.f;
              const meta = TIPO_FICHAJE[f.tipo] || TIPO_FICHAJE.salida;
              const esReal = fichajeEsReal(f);
              return (
                <div
                  key={f.id}
                  className={`flex items-center justify-between gap-2 rounded-xl border px-3 py-2.5 text-sm ${
                    f.es_autocierre ? "border-amber-200 bg-amber-50/50" : "border-zinc-100 bg-zinc-50/80"
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge variant="outline" className={meta.className}>{meta.label}</Badge>
                      {f.etiqueta_permiso && (
                        <Badge variant="outline" className="border-violet-300 bg-violet-50 text-[10px] text-violet-800">
                          {f.etiqueta_permiso}
                        </Badge>
                      )}
                      {f.es_informativo && (
                        <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[10px] text-rose-700">
                          Informativo
                        </Badge>
                      )}
                      {f.motivo_pausa && (
                        <span className="text-[11px] text-amber-700">{PAUSA_LABEL[f.motivo_pausa] || f.motivo_pausa}</span>
                      )}
                      {f.es_autocierre && (
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-[10px] text-amber-800">
                          Cierre automático
                        </Badge>
                      )}
                      {f.pendiente_aprobacion && (
                        <Badge variant="outline" className="border-sky-200 bg-sky-50 text-[10px] text-sky-700">
                          Pendiente aprobación
                        </Badge>
                      )}
                      {f.es_manual && !f.pendiente_aprobacion && !f.etiqueta_permiso && (
                        <span className="text-[10px] text-amber-600">Manual aprobado</span>
                      )}
                      {f.latitud != null && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-indigo-600">
                          <MapPin className="h-3 w-3" /> GPS
                        </span>
                      )}
                    </div>
                    {(f.justificacion || f.ruta_titulo || f.proyecto_nombre) && (
                      <p className="mt-0.5 truncate text-[11px] text-zinc-500">
                        {f.es_informativo
                          ? f.justificacion
                          : f.ruta_titulo
                            ? `Ruta · ${f.ruta_titulo}`
                            : f.proyecto_nombre
                              ? `${f.proyecto_codigo ? `${f.proyecto_codigo} · ` : ""}${f.proyecto_nombre}`
                              : f.justificacion}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="font-medium tabular-nums text-zinc-950">{fmtHora(f.timestamp)}</span>
                    {esReal && !f.pendiente_aprobacion && !f.es_informativo && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-700"
                        onClick={() => setCorreccion({ fichaje_id: f.id, timestamp: f.timestamp })}
                        data-testid={`corregir-picado-${f.id}`}
                      >
                        <PenLine className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <SolicitudPicadoManualDialog
        estado={estadoPanel}
        open={manualOpen}
        onOpenChange={(v) => {
          setManualOpen(v);
          if (!v) {
            setManualTipo(null);
            setManualTimestamp(null);
            if (!manualPendiente) setDiaEnfoque(hoyISO());
          }
        }}
        defaultTipo={manualTipo}
        defaultTimestamp={manualTimestamp}
        defaultDia={diaEnfoque !== hoyISO() ? diaEnfoque : undefined}
        onSuccess={refrescar}
      />
      <CorreccionFichajeDialog
        item={correccion}
        onClose={() => setCorreccion(null)}
        onSuccess={refrescar}
      />
    </div>
  );
}
